<template>
  <div class="rf">
    <p class="rf__lede">
      คืนเงินและเพิกถอนสิทธิ์ทันที — ใช้กับกรณีที่คลินิกร้องขอมาทางไลน์หรือโทร
      <strong>ผลมีทันที ไม่รอจบรอบ</strong> และย้อนกลับไม่ได้
    </p>

    <div v-if="!branchId" class="rf__empty">
      <p class="rf__empty-title">เริ่มจากหน้าค้นหาสาขา</p>
      <p class="rf__empty-detail">
        ไปที่แท็บ <strong>Branch Management</strong> ค้นชื่อคลินิกหรือชื่อสาขา แล้วกดปุ่ม
        <strong>คืนเงิน</strong> ที่แถวของสาขานั้น ระบบจะพามาที่นี่พร้อมข้อมูลครบ
      </p>
      <details class="rf__shortcut">
        <summary>มีเลข subscription อยู่แล้ว (จาก log)</summary>
        <div class="rf__lookup-row mt-2">
          <input
            v-model.trim="subscriptionId"
            class="form-control rf__input"
            type="text"
            inputmode="numeric"
            placeholder="เช่น 1842"
            @keyup.enter="loadQuote"
          />
          <b-button variant="outline-primary" :disabled="!subscriptionId || isLoading" @click="loadQuote">
            <b-spinner v-if="isLoading" small class="mr-1" />ดูยอดที่คืนได้
          </b-button>
        </div>
      </details>
    </div>

    <!--
      เลือกแพ็กที่จะคืน — ไม่ให้ระบบเลือกแทน เพราะสาขาหนึ่งมีหลายแถวพร้อมกันได้
      (ที่ใช้อยู่ + แถวอนาคตจากการดาวน์เกรด + แถวเก่าที่เพิ่งจบ) บนรายการเงินคนต้องเห็นก่อน
    -->
    <div v-else class="rf__pick">
      <div class="rf__branch">
        <span class="rf__branch-name">{{ branchLabel }}</span>
        <b-button size="sm" variant="link" class="rf__clear" @click="clearBranch">เปลี่ยนสาขา</b-button>
      </div>

      <b-spinner v-if="isLoadingList" small />
      <p v-else-if="subscriptions.length === 0" class="rf__hint">
        สาขานี้ไม่เคยซื้อแพ็กผ่านระบบเลย จึงไม่มีอะไรให้คืนเงิน
      </p>
      <table v-else class="table table-sm rf__table">
        <thead>
          <tr>
            <th>แพ็ก</th><th>รอบ</th><th>สถานะ</th><th>ช่วงเวลา</th><th class="text-right">จ่ายไป</th><th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="sub in subscriptions" :key="sub.subscriptionId" :class="{ 'is-picked': quote && quote.subscriptionId === sub.subscriptionId }">
            <td>
              {{ sub.planCode }}
              <span v-if="sub.isCurrent" class="badge badge-success ml-1">ใช้อยู่</span>
              <span v-if="sub.source !== 'SELF_SERVICE'" class="badge badge-secondary ml-1">{{ sub.source }}</span>
            </td>
            <td>{{ sub.billingCycle === "YEARLY" ? "รายปี" : "รายเดือน" }}</td>
            <td>
              {{ sub.status }}
              <span v-if="sub.recurringCancellationPending" class="badge badge-warning ml-1" title="ผู้ให้บริการยังถือแผนตัดเงินอยู่">RPP ค้าง</span>
            </td>
            <td>{{ sub.startDate }} → {{ sub.endDate || "ไม่มีวันหมดอายุ" }}</td>
            <td class="text-right">{{ sub.pricePaid }}</td>
            <td class="text-right">
              <b-button size="sm" variant="outline-primary" :disabled="isLoading" @click="pick(sub)">เลือก</b-button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <b-alert v-if="loadError" variant="danger" show class="mt-3">{{ loadError }}</b-alert>

    <div v-if="quote" class="rf__body">
      <dl class="rf__facts">
        <div><dt>สาขา</dt><dd>#{{ quote.branchId }}</dd></div>
        <div><dt>แพ็ก</dt><dd>{{ quote.planCode }}</dd></div>
        <div><dt>สถานะ</dt><dd>{{ quote.status }}</dd></div>
        <div><dt>รอบ</dt><dd>{{ quote.startDate }} → {{ quote.endDate || "ไม่มีวันหมดอายุ" }}</dd></div>
        <div><dt>เก็บไปจริง</dt><dd>{{ quote.pricePaid }} บาท</dd></div>
        <div>
          <dt>วันที่เหลือ</dt>
          <dd>{{ quote.daysRemaining === null ? "—" : `${quote.daysRemaining} จาก ${quote.totalDays} วัน` }}</dd>
        </div>
      </dl>

      <b-alert v-if="quote.recurringCancellationPending" variant="warning" show class="rf__pending">
        แผนตัดเงินที่ผู้ให้บริการถืออยู่ <strong>ยังไม่ถูกยกเลิก</strong> — ระบบจะบันทึกไว้ให้ตามเก็บ
        แต่ต้องไปกดยกเลิกในพอร์ทัลของผู้ให้บริการด้วย ไม่งั้นบัตรใบนี้จะถูกตัดรอบถัดไป
      </b-alert>

      <div class="rf__suggest">
        <span class="rf__suggest-label">ระบบเสนอ</span>
        <button type="button" class="rf__chip" @click="amountBaht = String(quote.suggested.full)">
          เต็มจำนวน {{ quote.suggested.full }} บาท
        </button>
        <button type="button" class="rf__chip" @click="amountBaht = String(quote.suggested.prorated)">
          ตามวันที่เหลือ {{ quote.suggested.prorated }} บาท
        </button>
        <button type="button" class="rf__chip" @click="amountBaht = '0'">
          ไม่คืนเงิน (0 บาท)
        </button>
      </div>
      <small class="rf__hint">
        เป็นข้อเสนอ ไม่ใช่ตัวเลือกที่ปิดตาย — กรอกยอดเองได้ เพดานคือ {{ quote.maxRefundable }} บาท
      </small>

      <div class="rf__form">
        <div class="rf__field">
          <label class="rf__label" for="rf-amount">ยอดที่คืนจริง (บาท)</label>
          <input id="rf-amount" v-model.trim="amountBaht" class="form-control" type="number" min="0" :max="quote.maxRefundable" />
        </div>
        <div class="rf__field rf__field--wide">
          <label class="rf__label" for="rf-reason">เหตุผล (บังคับ)</label>
          <input
            id="rf-reason"
            v-model.trim="reason"
            class="form-control"
            type="text"
            maxlength="255"
            placeholder="เช่น ซื้อผิดแพ็ก / ระบบใช้ไม่ได้ 3 วัน"
          />
        </div>
        <div class="rf__field rf__field--wide">
          <label class="rf__label" for="rf-ref">
            เลขอ้างอิงการคืนเงินจากพอร์ทัลผู้ให้บริการ
            <span v-if="needsRef" class="rf__req">บังคับเมื่อคืนเงินจริง</span>
          </label>
          <input id="rf-ref" v-model.trim="providerRefundRef" class="form-control" type="text" placeholder="คัดจากพอร์ทัล 2C2P" />
          <small class="rf__hint">
            ต้องไปกดคืนเงินในพอร์ทัลก่อน แล้วเอาเลขมากรอก — เลขนี้คือหลักฐานว่าเงินออกจริง
            ไม่ใช่แค่บันทึกว่าตั้งใจจะคืน
          </small>
        </div>
      </div>

      <b-alert v-if="submitError" variant="danger" show class="mt-2">{{ submitError }}</b-alert>
      <b-alert v-if="result" variant="success" show class="mt-2">
        คืนเงินเรียบร้อย — ใบคืนเงิน {{ result.refundOrderNumber }} · {{ result.amountRefunded }} บาท ·
        สิทธิ์ถูกเพิกถอนแล้ว (สิ้นสุด {{ result.subscription.endDate }})
        <template v-if="result.recurringCancellationPending">
          <br /><strong>ยังต้องไปยกเลิกแผนตัดเงินในพอร์ทัลด้วย</strong>
        </template>
      </b-alert>

      <div class="rf__actions">
        <b-button variant="danger" :disabled="!canSubmit" @click="submit">
          <b-spinner v-if="isSubmitting" small class="mr-1" />
          {{ isSubmitting ? "กำลังดำเนินการ…" : "คืนเงินและเพิกถอนสิทธิ์" }}
        </b-button>
        <span class="rf__warn">ทำแล้วย้อนกลับไม่ได้ ตรวจยอดและเลขอ้างอิงอีกครั้งก่อนกด</span>
      </div>
    </div>
  </div>
</template>

<script>
import SuperadminService from "@/services/SuperadminService";

/** ชื่อที่ทีมใช้ยืนยันว่าหยิบถูกสาขา — เลขสาขาอย่างเดียวยืนยันอะไรไม่ได้ */
function describeBranch(b) {
  if (!b) return "";
  const clinic = b.clinicName || b.clinicCode || "";
  const branch = b.branchName || b.branchNameTh || b.branchCode || `#${b.branchId}`;
  return clinic ? `${clinic} — ${branch}` : branch;
}

export default {
  name: "BillingRefundPanel",
  props: {
    // สาขาที่ถูกเลือกมาจากหน้าค้นหา — null = ยังไม่ได้เลือก แสดงคำแนะนำให้ไปเริ่มที่นั่น
    branch: { type: Object, default: null },
  },
  data() {
    return {
      branchId: this.branch ? this.branch.branchId : null,
      branchLabel: this.branch ? describeBranch(this.branch) : "",
      subscriptions: [],
      isLoadingList: false,
      subscriptionId: "",
      quote: null,
      isLoading: false,
      loadError: "",
      amountBaht: "",
      reason: "",
      providerRefundRef: "",
      isSubmitting: false,
      submitError: "",
      result: null,
    };
  },
  watch: {
    // แท็บนี้ถูก mount ใหม่ทุกครั้งที่เปิด (lazy) แต่ถ้าทีมกดปุ่มคืนเงินของอีกสาขา
    // ขณะที่แท็บเปิดค้างอยู่ ต้องล้างของเก่าทิ้งทั้งหมด ไม่ใช่เอาสาขาใหม่ไปทับ
    // ฟอร์มที่กรอกไว้ให้สาขาเดิม
    branch: {
      immediate: false,
      handler(next) {
        this.resetAll();
        if (!next) return;
        this.branchId = next.branchId;
        this.branchLabel = describeBranch(next);
        this.loadSubscriptions();
      },
    },
  },
  created() {
    if (this.branchId) this.loadSubscriptions();
  },
  computed: {
    amountNumber() {
      const n = Number(this.amountBaht);
      return Number.isFinite(n) ? n : NaN;
    },
    // เลขอ้างอิงจำเป็นเฉพาะตอนมีเงินเคลื่อนจริง — ยอด 0 คือการเพิกถอนเฉย ๆ
    needsRef() {
      return this.amountNumber > 0;
    },
    canSubmit() {
      if (this.isSubmitting || !this.quote) return false;
      if (!Number.isFinite(this.amountNumber) || this.amountNumber < 0) return false;
      if (this.amountNumber > this.quote.maxRefundable) return false;
      if (!this.reason) return false;
      if (this.needsRef && !this.providerRefundRef) return false;
      return true;
    },
  },
  methods: {
    /** ล้างทุกอย่างที่ผูกกับสาขาเดิม — ห้ามเหลือยอดหรือเหตุผลของสาขาก่อนหน้าค้างไว้ */
    resetAll() {
      this.branchId = null;
      this.branchLabel = "";
      this.subscriptions = [];
      this.subscriptionId = "";
      this.quote = null;
      this.loadError = "";
      this.amountBaht = "";
      this.reason = "";
      this.providerRefundRef = "";
      this.submitError = "";
      this.result = null;
    },

    clearBranch() {
      this.resetAll();
      this.$emit("cleared");
    },

    async loadSubscriptions() {
      this.isLoadingList = true;
      this.loadError = "";
      try {
        const res = await SuperadminService.listBranchSubscriptions(this.branchId);
        const data = res.data.data || res.data;
        this.subscriptions = data.subscriptions || [];
        this.branchLabel = describeBranch(data.branch) || this.branchLabel;
      } catch (err) {
        this.loadError = this.apiMessage(err, "อ่านรายการแพ็กของสาขานี้ไม่ได้");
      } finally {
        this.isLoadingList = false;
      }
    },

    /** ทีมเลือกแถวที่จะคืน — จากนี้เข้า flow เดิมทุกอย่าง */
    pick(sub) {
      this.subscriptionId = String(sub.subscriptionId);
      this.loadQuote();
    },

    // BE ตอบ { errorCode, errorMessage } ไม่ใช่ { message }
    apiMessage(err, fallback) {
      const data = err && err.response && err.response.data;
      return (data && (data.errorMessage || data.message)) || fallback;
    },
    async loadQuote() {
      this.isLoading = true;
      this.loadError = "";
      this.quote = null;
      this.result = null;
      this.submitError = "";
      try {
        const res = await SuperadminService.getRefundQuote(this.subscriptionId);
        this.quote = res.data.data || res.data;
        // ตั้งค่าเริ่มต้นเป็นยอดตามวันที่เหลือ ซึ่งเป็นคำตอบที่ถูกบ่อยที่สุด
        // แต่ยังแก้ได้ — ไม่ได้ล็อกไว้
        this.amountBaht = String(this.quote.suggested.prorated);
      } catch (err) {
        this.loadError = this.apiMessage(err, "อ่านข้อมูล subscription ไม่ได้");
      } finally {
        this.isLoading = false;
      }
    },
    async submit() {
      if (!this.canSubmit) return;
      this.isSubmitting = true;
      this.submitError = "";
      try {
        const res = await SuperadminService.refundSubscription(this.quote.subscriptionId, {
          amountBaht: this.amountNumber,
          reason: this.reason,
          providerRefundRef: this.providerRefundRef || undefined,
        });
        this.result = res.data.data || res.data;
        // โหลดใหม่เพื่อให้สถานะบนจอตรงกับความจริงหลังเพิกถอน
        const res2 = await SuperadminService.getRefundQuote(this.quote.subscriptionId);
        this.quote = res2.data.data || res2.data;
      } catch (err) {
        this.submitError = this.apiMessage(err, "คืนเงินไม่สำเร็จ");
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.rf {
  max-width: 880px;

  &__lede {
    font-size: 14px;
    color: #475467;
  }
  &__empty {
    margin-top: 12px;
    padding: 16px 18px;
    border: 1px dashed #d8dde5;
    border-radius: 12px;
    background: #fbfcfd;
  }
  &__empty-title {
    margin: 0 0 4px;
    font-weight: 600;
    color: #101828;
  }
  &__empty-detail {
    margin: 0;
    font-size: 13.5px;
    color: #475467;
  }
  &__shortcut {
    margin-top: 12px;
    font-size: 13px;
    color: #98a2b3;

    summary {
      cursor: pointer;
    }
  }
  &__pick {
    margin-top: 12px;
  }
  &__branch {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
  }
  &__branch-name {
    font-size: 15px;
    font-weight: 600;
    color: #101828;
  }
  &__clear {
    padding: 0;
    font-size: 12.5px;
  }
  &__table {
    font-size: 13px;

    tr.is-picked {
      background: #e8f2ff;
    }
  }
  &__lookup {
    margin-top: 12px;
  }
  &__lookup-row {
    display: flex;
    gap: 10px;
    align-items: center;
  }
  &__input {
    max-width: 200px;
  }
  &__label {
    display: block;
    font-size: 12.5px;
    font-weight: 600;
    color: #475467;
    margin-bottom: 4px;
  }
  &__req {
    margin-left: 6px;
    font-weight: 500;
    color: #b42318;
  }
  &__hint {
    display: block;
    margin-top: 5px;
    color: #98a2b3;
  }
  &__body {
    margin-top: 20px;
    padding: 18px;
    border: 1px solid #e3e6eb;
    border-radius: 12px;
    background: #fff;
  }
  &__facts {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 10px 20px;
    margin: 0 0 14px;

    dt {
      font-size: 12px;
      font-weight: 600;
      color: #98a2b3;
      margin: 0;
    }
    dd {
      margin: 0;
      font-size: 14px;
      color: #101828;
    }
  }
  &__pending {
    font-size: 13.5px;
  }
  &__suggest {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
  }
  &__suggest-label {
    font-size: 12.5px;
    font-weight: 600;
    color: #98a2b3;
  }
  &__chip {
    border: 1px solid #d8dde5;
    background: #f8f9fb;
    border-radius: 999px;
    padding: 5px 13px;
    font-size: 13px;
    color: #475467;
    cursor: pointer;

    &:hover {
      border-color: #007bff;
      color: #0056b3;
    }
  }
  &__form {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 14px;
    margin-top: 16px;
  }
  &__field--wide {
    grid-column: 1 / -1;
  }
  &__actions {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-top: 18px;
  }
  &__warn {
    font-size: 12.5px;
    color: #b42318;
  }
}
</style>
